import React, { useState } from 'react';
import { X, Star, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const StarRating = ({ rating, setRating }) => {
  return (
    <div className="flex items-center gap-2">
      {[...Array(5)].map((_, index) => {
        const starValue = index + 1;
        return (
          <button
            type="button"
            key={starValue}
            onClick={() => setRating(starValue)}
            className="transition-transform transform hover:scale-110"
          >
            <Star
              size={36}
              className={
                starValue <= rating
                  ? 'text-yellow-500 fill-yellow-500'
                  : 'text-neutral-300'
              }
            />
          </button>
        );
      })}
    </div>
  );
};

const ReviewModal = ({ booking, onClose, onSubmitReview }) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) {
      setError(t('reviewmodal.error'));
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      // Pass the data up to the parent to be saved
      await onSubmitReview({
        rating,
        comment,
      });
      setLoading(false);
      onClose(); // Close the modal on success
    } catch (err) {
      console.error("Error submitting review:", err);
      setError(t('reviewmodal.errorSubmit'));
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-[60] animate-fade-in overflow-y-auto">
      <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-neutral-700/50 max-w-lg w-full p-8 animate-slide-up relative my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition"
        >
          <X size={24} />
        </button>

        <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-2">{t('reviewmodal.title')}</h2>
        <p className="text-neutral-600 dark:text-neutral-400 mb-6">{t('reviewmodal.subtitle', { stadiumName: booking.stadiumName })}</p>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4 text-center">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="flex flex-col items-center">
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-3">{t('reviewmodal.ratingLabel')}</label>
            <StarRating rating={rating} setRating={setRating} />
          </div>

          <div>
            <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
              {t('reviewmodal.commentLabel')}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full h-32 px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition"
              placeholder={t('reviewmodal.commentPlaceholder')}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-4 border-2 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition"
            >
              {t('reviewmodal.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  <span>{t('reviewmodal.submitting')}</span>
                </>
              ) : (
                t('reviewmodal.submit')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;